import { differenceInDays, isBefore } from "date-fns";
import { createLocalDate } from "@/utils/dateUtils";
import type { ControleProjeto } from "@/types/controle-projeto";

export type EstadoProjeto =
  | "rascunho"
  | "planejado"
  | "a_iniciar"
  | "em_andamento"
  | "fixo"
  | "vencido"
  | "nao_renova"
  | "renovado"
  | "finalizado";

export interface EstadoProjetoCalculado {
  estado: EstadoProjeto;
  diasRestantes: number | null;
}

type ProjetoDatado = Pick<ControleProjeto, "status" | "data_inicio" | "data_conclusao" | "modalidade">;

// Fonte única do estado exibido (página, card, kanban, ficha do freelancer).
// 'renovado' e 'encerrado' vêm do banco; o resto é derivado das datas. Um 'ativo'
// com data vencida NÃO é finalizado: é "vencido", fila de decisão (Finalizar/Renovar).
// Um 'encerrado' com conclusão ainda por vir é "nao_renova": o freelancer trabalha até
// a data e o card vira "finalizado" sozinho quando ela passa (sem cron).
// "fixo" é a vaga fixa (modalidade); um frila sem término segue em andamento.
// 'planejado' é uma renovação já combinada: só vira 'ativo' quando alguém confirma.
export function calcularEstadoProjeto(
  projeto: ProjetoDatado,
  hoje: Date = new Date(),
): EstadoProjetoCalculado {
  const dataInicio = projeto.data_inicio ? createLocalDate(projeto.data_inicio) : null;
  const dataConclusao = projeto.data_conclusao ? createLocalDate(projeto.data_conclusao) : null;
  const diasRestantes = dataConclusao ? differenceInDays(dataConclusao, hoje) : null;

  if (projeto.status === "renovado") return { estado: "renovado", diasRestantes };
  if (projeto.status === "planejado") return { estado: "planejado", diasRestantes };
  if (projeto.status === "encerrado") {
    const aindaEmCurso = diasRestantes !== null && diasRestantes >= 0;
    return { estado: aindaEmCurso ? "nao_renova" : "finalizado", diasRestantes };
  }
  if (!dataInicio) return { estado: "rascunho", diasRestantes };
  if (projeto.modalidade === "fixa") return { estado: "fixo", diasRestantes: null };
  if (isBefore(hoje, dataInicio)) return { estado: "a_iniciar", diasRestantes };
  if (diasRestantes !== null && diasRestantes < 0) return { estado: "vencido", diasRestantes };
  return { estado: "em_andamento", diasRestantes };
}

// Histórico = já acabou de verdade. Renovado com conclusão ainda por vir segue visível
// por padrão (a renovação foi cadastrada antes do fim). Espelha os filtros das queries.
export function isProjetoHistorico({ estado, diasRestantes }: EstadoProjetoCalculado): boolean {
  if (estado === "finalizado") return true;
  return estado === "renovado" && diasRestantes !== null && diasRestantes < 0;
}

export function podeFinalizar(estado: EstadoProjeto): boolean {
  return estado === "em_andamento" || estado === "vencido" || estado === "fixo" || estado === "a_iniciar";
}

export function podeRenovar(estado: EstadoProjeto): boolean {
  return estado !== "renovado" && estado !== "rascunho" && estado !== "planejado";
}

export function jaTerminou(estado: EstadoProjeto): boolean {
  return estado === "finalizado" || estado === "renovado";
}
